import { PathLayer, ScatterplotLayer, TripsLayer, type LayersList, type PickingInfo } from "deck.gl";
import type { MapRoute, MapRouteData, RouteLeg } from "./map-route";
import { withDeckAlpha, type MapThemePalette } from "./map-theme";
import { ROUTE_TRAIL_LENGTH, type RouteAnimationFrame } from "./route-animation";
import { getRouteArcPosition } from "./route-arc";

interface CreateRouteMapLayersArgs<Route extends MapRoute> {
  data: MapRouteData<Route>;
  animationFrame: RouteAnimationFrame | null;
  palette: MapThemePalette;
  onHover?: (info: PickingInfo<Route>) => void;
}

const LAYER_PARAMETERS = { depthWriteEnabled: false };
const MARKER_RADIUS_PIXELS = 6;
const ARRIVAL_RIPPLE_GROWTH_PIXELS = 20;

export function createRouteMapLayers<Route extends MapRoute>({
  data: { routes, completedLegs, remainingLegs, currentRoutes },
  animationFrame,
  palette,
  onHover,
}: CreateRouteMapLayersArgs<Route>): LayersList {
  const isDark = palette.mode === "dark";
  const { primaryRgb } = palette.accent;
  const { headRgb, trailRgb, glowRgb } = palette.route;
  const arrivalProgress = animationFrame?.arrivalProgress ?? null;
  const headFraction = animationFrame?.headFraction ?? null;
  const handleLegHover = (info: PickingInfo<RouteLeg<Route>>) => onHover?.({ ...info, object: info.object?.route });

  return [
    new PathLayer<RouteLeg<Route>>({
      id: "route-completed",
      data: completedLegs,
      getPath: ({ arc }) => arc.path,
      getColor: withDeckAlpha(primaryRgb, isDark ? 40 : 55),
      getWidth: 1.5,
      widthUnits: "pixels",
      capRounded: true,
      jointRounded: true,
      billboard: true,
      pickable: Boolean(onHover),
      onHover: handleLegHover,
      parameters: LAYER_PARAMETERS,
    }),
    new PathLayer<RouteLeg<Route>>({
      id: "route-track",
      data: remainingLegs,
      getPath: ({ arc }) => arc.path,
      getColor: animationFrame ? withDeckAlpha(trailRgb, isDark ? 80 : 100) : withDeckAlpha(headRgb, 230),
      getWidth: 3,
      widthUnits: "pixels",
      capRounded: true,
      jointRounded: true,
      billboard: true,
      pickable: Boolean(onHover),
      onHover: handleLegHover,
      parameters: LAYER_PARAMETERS,
    }),
    animationFrame && [
      new TripsLayer<RouteLeg<Route>>({
        id: "route-trail-glow",
        data: remainingLegs,
        getPath: ({ arc }) => arc.path,
        getTimestamps: ({ arc }) => arc.timestamps,
        getColor: withDeckAlpha(glowRgb, isDark ? 90 : 60),
        getWidth: 10,
        widthUnits: "pixels",
        capRounded: true,
        jointRounded: true,
        billboard: true,
        fadeTrail: true,
        trailLength: ROUTE_TRAIL_LENGTH,
        currentTime: animationFrame.currentTime,
        parameters: LAYER_PARAMETERS,
      }),
      new TripsLayer<RouteLeg<Route>>({
        id: "route-trail",
        data: remainingLegs,
        getPath: ({ arc }) => arc.path,
        getTimestamps: ({ arc }) => arc.timestamps,
        getColor: withDeckAlpha(headRgb, 255),
        getWidth: 4,
        widthUnits: "pixels",
        capRounded: true,
        jointRounded: true,
        billboard: true,
        fadeTrail: true,
        trailLength: ROUTE_TRAIL_LENGTH,
        currentTime: animationFrame.currentTime,
        parameters: LAYER_PARAMETERS,
      }),
    ],
    new ScatterplotLayer<Route>({
      id: "route-destination",
      data: routes,
      getPosition: (route) => route.destination,
      getFillColor: withDeckAlpha(primaryRgb, 255),
      getLineColor: withDeckAlpha(headRgb, 255),
      getRadius: MARKER_RADIUS_PIXELS,
      getLineWidth: 2,
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      billboard: true,
      stroked: true,
      pickable: Boolean(onHover),
      onHover,
      parameters: LAYER_PARAMETERS,
    }),
    arrivalProgress !== null &&
      new ScatterplotLayer<RouteLeg<Route>>({
        id: "route-arrival-ripple",
        data: remainingLegs,
        getPosition: ({ arc }) => arc.destination,
        getLineColor: withDeckAlpha(primaryRgb, Math.round(200 * (1 - arrivalProgress))),
        getRadius: MARKER_RADIUS_PIXELS + ARRIVAL_RIPPLE_GROWTH_PIXELS * arrivalProgress,
        getLineWidth: 2,
        radiusUnits: "pixels",
        lineWidthUnits: "pixels",
        stroked: true,
        filled: false,
        parameters: LAYER_PARAMETERS,
      }),
    headFraction !== null && [
      new ScatterplotLayer<RouteLeg<Route>>({
        id: "route-head-halo",
        data: remainingLegs,
        getPosition: ({ arc }) => getRouteArcPosition(arc.origin, arc.destination, headFraction),
        updateTriggers: { getPosition: headFraction },
        getFillColor: withDeckAlpha(glowRgb, 90),
        getRadius: 9,
        radiusUnits: "pixels",
        billboard: true,
        parameters: LAYER_PARAMETERS,
      }),
      new ScatterplotLayer<RouteLeg<Route>>({
        id: "route-head",
        data: remainingLegs,
        getPosition: ({ arc }) => getRouteArcPosition(arc.origin, arc.destination, headFraction),
        updateTriggers: { getPosition: headFraction },
        getFillColor: withDeckAlpha(headRgb, 255),
        getRadius: 3.5,
        radiusUnits: "pixels",
        billboard: true,
        parameters: LAYER_PARAMETERS,
      }),
    ],
    new ScatterplotLayer<Route>({
      id: "route-current-halo",
      data: currentRoutes,
      getPosition: (route) => route.current!,
      getFillColor: withDeckAlpha(primaryRgb, 55),
      getRadius: 14,
      radiusUnits: "pixels",
      billboard: true,
      parameters: LAYER_PARAMETERS,
    }),
    new ScatterplotLayer<Route>({
      id: "route-current",
      data: currentRoutes,
      getPosition: (route) => route.current!,
      getFillColor: withDeckAlpha(primaryRgb, 255),
      getLineColor: withDeckAlpha(headRgb, 255),
      getRadius: 7,
      getLineWidth: 2,
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      billboard: true,
      stroked: true,
      pickable: Boolean(onHover),
      onHover,
      parameters: LAYER_PARAMETERS,
    }),
    new ScatterplotLayer<Route>({
      id: "route-origin",
      data: routes,
      getPosition: (route) => route.origin,
      getLineColor: withDeckAlpha(primaryRgb, 255),
      getRadius: MARKER_RADIUS_PIXELS + 5,
      getLineWidth: 2,
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      billboard: true,
      stroked: true,
      filled: false,
      pickable: Boolean(onHover),
      onHover,
      parameters: LAYER_PARAMETERS,
    }),
  ];
}
